import { useRoasterProfile } from "@bcc/contexts/roaster-profile-context";
import { cn } from "@bcc/lib/utils";
import {
  Bell,
  CreditCard,
  DollarSign,
  Home,
  LayoutDashboard,
  Package,
  Settings,
  ShoppingCart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  setAppSidebarNavItemTestId,
  setAppSidebarTestId,
} from "./app-sidebar.utils";

const navigation = [
  {
    nameKey: "sidebar.navigation.home",
    href: "/",
    icon: Home,
  },
  {
    nameKey: "sidebar.navigation.orders",
    href: "/orders",
    icon: ShoppingCart,
    badge: 7,
    restrictedFor: ["SS"],
  },
  {
    nameKey: "sidebar.navigation.products",
    href: "/products",
    icon: Package,
  },
  {
    nameKey: "sidebar.navigation.payments",
    href: "/finance/invoices",
    icon: CreditCard,
  },
];

const bonusNavigation = [
  {
    nameKey: "sidebar.navigation.dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    nameKey: "sidebar.navigation.notifications",
    href: "/notifications",
    icon: Bell,
  },
  {
    nameKey: "sidebar.navigation.financeSettings",
    href: "/finance/settings",
    icon: DollarSign,
  },
];

const settingsNavigation = [
  {
    nameKey: "sidebar.navigation.preferences",
    href: "/preferences",
    icon: Settings,
  },
];

interface AppSidebarProps {
  testId?: string;
}

export function AppSidebar({ testId }: AppSidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { profileType } = useRoasterProfile();

  return (
    <aside
      data-testid={testId ?? setAppSidebarTestId("container")}
      className="fixed left-0 top-[72px] z-40 h-[calc(100vh-72px)] w-[264px] border-r border-border-subtle bg-surface-2"
    >
      <nav className="flex h-full flex-col gap-2 p-4">
        {/* Main Navigation */}
        <div className="flex-1 space-y-1">
          {navigation
            .filter((item) => !item.restrictedFor?.includes(profileType))
            .map((item) => {
              const isActive =
                pathname === item.href ||
                (item.href !== "/" && pathname.startsWith(item.href));
              const name = t(item.nameKey) as string;
              return (
                <Link
                  key={item.nameKey}
                  to={item.href}
                  data-testid={setAppSidebarNavItemTestId(name)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    "hover:bg-accent/10",
                    isActive
                      ? "bg-accent/10 text-accent-strong border-l-4 border-accent-strong -ml-4 pl-[calc(0.75rem-4px)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                  <span>{name}</span>
                  {item.badge && (
                    <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
        </div>

        <div className="border-t border-border-subtle pt-4">
          <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("sidebar.sections.bonus")}
          </div>
          <div className="space-y-1">
            {bonusNavigation.map((item) => {
              const isActive = pathname === item.href;
              const name = t(item.nameKey) as string;
              return (
                <Link
                  key={item.nameKey}
                  to={item.href}
                  data-testid={setAppSidebarNavItemTestId(name)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    "hover:bg-accent/10",
                    isActive
                      ? "bg-accent/10 text-accent-strong"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                  <span>{name}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Settings Section */}
        <div className="border-t border-border-subtle pt-4">
          <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("sidebar.sections.settings")}
          </div>
          <div className="space-y-1">
            {settingsNavigation.map((item) => {
              const isActive = pathname === item.href;
              const name = t(item.nameKey) as string;
              return (
                <Link
                  key={item.nameKey}
                  to={item.href}
                  data-testid={setAppSidebarNavItemTestId(name)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                    "hover:bg-accent/10",
                    isActive
                      ? "bg-accent/10 text-accent-strong"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                  <span>{name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </aside>
  );
}
